import {
  BasicUseDocument,
  emptyBasicUseDocument,
  MessageThread,
  messageThreadCollections,
  PopulatedThreadMessage,
  SelectedMessage,
  ThreadType,
} from '@hb/shared'
import React, { createContext, PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { useFetchData } from '../../contexts/db/FetchDataContext'
import { emptyUseThreadMessagesNew } from '../constants'
import { useNewThreadMessages } from '../hooks/newMessages'
import { UseThreadMessagesNew } from '../types'
import { useThreadsList } from './useThreadsList'

export interface ThreadViewProviderProps {
  onBack: () => void
  threadId?: string
  width: number
  threadType: ThreadType
  uid: string | null
  isOpen: boolean
  assessmentThreadUnread?: boolean
  // deprecated — ThreadViewProvider creates its own useNewThreadMessages instance;
  // consumers should read messages from the context rather than passing them in
  messages?: UseThreadMessagesNew
}
export interface ThreadViewContextData
  extends Omit<ThreadViewProviderProps, 'selectMessage' | 'width'> {
  threadId: string
  threadType: ThreadType
  messages: UseThreadMessagesNew
  isOpen: boolean
  thread: BasicUseDocument<MessageThread>
  onBack: () => void
  addingMembers: boolean
  setAddingMembers: (adding: boolean) => void
  replyingTo: PopulatedThreadMessage | null
  setReplyingTo: (replyingTo: PopulatedThreadMessage | null) => void
  editingMessage: PopulatedThreadMessage | null
  setEditingMessage: (editingMessage: PopulatedThreadMessage | null) => void
  editingTitle: boolean
  setEditingTitle: (editingTitle: boolean) => void
  manuallyMarkedUnread: boolean
  setManuallyMarkedUnread: (manuallyMarkedUnread: boolean) => void
  searchOpen: boolean
  setSearchOpen: (searchOpen: boolean) => void
}

export const ThreadViewContext = createContext<ThreadViewContextData>({
  uid: null,
  threadId: '',
  threadType: ThreadType.GROUP,
  messages: emptyUseThreadMessagesNew,
  thread: emptyBasicUseDocument,
  onBack: () => {},
  isOpen: false,
  addingMembers: false,
  setAddingMembers: () => {},
  replyingTo: null,
  setReplyingTo: () => {},
  editingMessage: null,
  editingTitle: false,
  setEditingTitle: () => {},
  setEditingMessage: () => {},
  manuallyMarkedUnread: false,
  setManuallyMarkedUnread: () => {},
  searchOpen: false,
  setSearchOpen: () => {},
})

const useScrollToThreadResult = (selectMessage: (selectedMessage: SelectedMessage) => void) => {
  const {
    widget: { searchResult, clearSearchResult },
  } = useThreadsList()
  useEffect(() => {
    if (searchResult) {
      selectMessage({
        messageGroupId: searchResult.messageGroupId,
        messageId: searchResult.messageId,
        selectedOn: Date.now(),
        messageGroupCreatedOn: searchResult.message.messageGroupCreatedOn,
      })
      clearSearchResult()
    }
  }, [searchResult, clearSearchResult, selectMessage])
}

export const ThreadViewProvider = ({
  threadType,
  onBack,
  threadId,
  uid,
  isOpen,
  children,
}: PropsWithChildren<ThreadViewProviderProps>) => {
  const threadCollection = messageThreadCollections[threadType]
  const { useDocument } = useFetchData()
  // const [fetchAllMessages, setFetchAllMessages] = useState(!!fromSearch)
  const [editingTitle, setEditingTitle] = useState(false)
  const threadData = useDocument<MessageThread>(threadCollection, threadId)

  const [searchOpen, setSearchOpen] = useState(false)

  const messages = useNewThreadMessages(uid, threadType, threadId ?? null)
  const [manuallyMarkedUnread, setManuallyMarkedUnread] = useState(false)

  useEffect(() => {
    if (!threadId || !threadType) setManuallyMarkedUnread(false)
  }, [threadId, threadType])

  useScrollToThreadResult(messages.selectMessage)

  const [addingMembers, setAddingMembers] = useState(false)

  const [replyingTo, setReplyingTo] = useState<PopulatedThreadMessage | null>(null)

  const [editingMessage, setEditingMessage] = useState<PopulatedThreadMessage | null>(null)

  const contextData = useMemo(
    () => ({
      threadId: threadId ?? '',
      threadType,
      thread: threadData,
      isOpen,
      messages,
      onBack,
      replyingTo,
      setReplyingTo: (message: PopulatedThreadMessage | null) => {
        if (message) {
          setEditingMessage(null)
          setReplyingTo(message)
        } else {
          setReplyingTo(null)
        }
      },
      editingMessage,
      setEditingMessage: (message: PopulatedThreadMessage | null) => {
        if (message) {
          setReplyingTo(null)
          setEditingMessage(message)
        } else {
          setEditingMessage(null)
        }
      },
      uid,
      editingTitle,
      setEditingTitle,
      addingMembers,
      searchOpen,
      setSearchOpen,
      setAddingMembers,
      manuallyMarkedUnread,
      setManuallyMarkedUnread,
    }),
    [
      threadId,
      threadType,
      editingMessage,
      editingTitle,
      searchOpen,
      uid,
      setSearchOpen,
      threadData,
      messages,
      replyingTo,
      addingMembers,
      setAddingMembers,
      isOpen,
      onBack,
      manuallyMarkedUnread,
    ],
  )
  return <ThreadViewContext.Provider value={contextData}>{children}</ThreadViewContext.Provider>
}
